import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, Typography, useMediaQuery } from '@mui/material';
import { AddOutlined, CurrencyExchangeOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import ReportPeriodService from '../../../shared/services/report-period/report-period.service';
import { IExchangeRateApplyJob, IExchangeRateApplyPreview, IReportPeriodExchangeRate } from '../../../shared/services/report-period/report-period.model';
import LocationService from '../../../shared/services/location/location.service';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { RecalculationHistoryTable } from './recalculation-history-table';
import { TableDisplay } from '../miscellaneous-components/table-display';
import { TableActionButtons } from '../miscellaneous-components/table-action-buttons';
import { LoadingSpinner } from '../display-elements-components/loading-spinner';
import { ExchangeRateDialog, ICurrencyOption } from '../../dialogs/settings/report-period/exchange-rate-dialog';
import gaiaTheme from '../../../root-components/theme';

interface ReportPeriodExchangeRatesSectionProps {
  reportPeriodId: string;
  locked?: boolean;
}

const formatRate = (value: number) =>
  new Intl.NumberFormat('en-GB', { maximumFractionDigits: 6 }).format(value);

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

const describeCompleted = (job: IExchangeRateApplyJob): string => {
  const returned = job.returnedToDailyCount || 0;
  if (returned > 0 && returned === job.recalculatedCount) return `${plural(returned, 'record', 'records')} returned to daily market rates`;
  if (returned > 0) return `${plural(job.recalculatedCount, 'record', 'records')} recalculated, ${returned} of them returned to daily market rates`;
  return `${plural(job.recalculatedCount, 'record', 'records')} recalculated at the fixed rates`;
};

// "Label (count); Label (count)" for a reason map, using the labels the API sends.
const describeReasons = (reasons: Record<string, number> | null | undefined, labels: Record<string, string>) =>
  Object.entries(reasons || {})
    .filter(([, count]) => count > 0)
    .map(([code, count]) => `${labels[code] || 'Other reason'} (${count})`)
    .join('; ');

// Fixed exchange rates for one reporting period, on the period's Conversion Factors tab. Every spend
// calculation in the period that converts a listed pair (or its inverse) uses the fixed rate instead
// of the daily market rate. Saving a rate recalculates nothing, which the caption says.
// The rates are an override of automatic behaviour, so they sit behind a tick box: unticked, the
// section says conversion is automatic and asks for nothing.
export const ReportPeriodExchangeRatesSection: React.FC<ReportPeriodExchangeRatesSectionProps> = ({
  reportPeriodId,
  locked = false,
}) => {
  const reportPeriodService = new ReportPeriodService();
  const locationService = LocationService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  const [rates, setRates] = useState<IReportPeriodExchangeRate[]>([]);
  const [isLocked, setIsLocked] = useState<boolean>(locked);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [currencyOptions, setCurrencyOptions] = useState<ICurrencyOption[]>([]);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editRate, setEditRate] = useState<IReportPeriodExchangeRate | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [applyJobs, setApplyJobs] = useState<IExchangeRateApplyJob[]>([]);
  const [reasonLabels, setReasonLabels] = useState<Record<string, string>>({});
  const [isPreparingApply, setIsPreparingApply] = useState<boolean>(false);
  const [pollJobId, setPollJobId] = useState<string | null>(null);
  // Ticked by the user before any rate exists. Once a rate is saved the box follows the rates.
  const [overrideOpened, setOverrideOpened] = useState<boolean>(false);
  // Records still on a fixed rate that has been deleted. They stay there until returned.
  const [strandedCount, setStrandedCount] = useState<number>(0);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchRates = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      const response = await reportPeriodService.getExchangeRates(reportPeriodId);
      setRates(response.rates || []);
      setIsLocked(!!response.locked || locked);
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
      setErrorMessage('Failed to load exchange rates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Only asked for when it can matter: a period with no job running.
  const fetchStranded = async () => {
    try {
      const preview = await reportPeriodService.getExchangeRateApplyPreview(reportPeriodId);
      setStrandedCount((preview.toReturnToDaily || 0) + (preview.uncommittedLedgerLinesToReturn || 0));
    } catch (error) {
      setStrandedCount(0);
    }
  };

  const fetchApplyJobs = async () => {
    try {
      const response = await reportPeriodService.getExchangeRateApplyJobs(reportPeriodId);
      setApplyJobs(response.jobs || []);
      setReasonLabels(response.reasonLabels || {});
      const running = (response.jobs || []).find((job) => job.status === 'PENDING' || job.status === 'IN_PROGRESS');
      if (running) setPollJobId(running.id);
    } catch (error) {
      setApplyJobs([]);
    }
  };

  useEffect(() => {
    if (reportPeriodId) {
      fetchRates();
      fetchApplyJobs();
      fetchStranded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodId, locked]);

  // Follow a queued or running recalculation until it finishes, then say how it went.
  useEffect(() => {
    if (!pollJobId) return undefined;
    pollTimerRef.current = setInterval(async () => {
      try {
        const job = await reportPeriodService.getExchangeRateApplyJob(reportPeriodId, pollJobId);
        if (job.status === 'COMPLETED' || job.status === 'FAILED') {
          if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          setPollJobId(null);
          fetchApplyJobs();
          fetchStranded();
          userNotificationsContext.openSnackbar(job.status === 'COMPLETED'
            ? { open: true, message: describeCompleted(job) }
            : { open: true, message: job.errorMessage || 'The recalculation failed', backgroundColor: 'text.primary' });
        }
      } catch (error) {
        console.error('Error polling the exchange rate recalculation:', error);
      }
    }, 2000);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollJobId, reportPeriodId]);

  const hasActiveJob = !!pollJobId || applyJobs.some((job) => job.status === 'PENDING' || job.status === 'IN_PROGRESS');

  // Preview first, then confirm in plain words what will and will not change.
  const buildConfirmation = (preview: IExchangeRateApplyPreview): string[] => {
    const labels = preview.reasonLabels || {};
    const pairsText = preview.pairs
      .filter((pair) => pair.recordCount > 0)
      .map((pair) => `1 ${pair.fromCurrencyCode} = ${formatRate(pair.rate)} ${pair.toCurrencyCode}`)
      .join(' and ');
    const lines: string[] = [];
    if (preview.toChange > 0) {
      lines.push(`${plural(preview.toChange, 'spend record', 'spend records')} in this period will be recalculated at ${pairsText}. Reported totals for this period will change. Factors and inflation adjustments stay as they are.`);
    }
    if (preview.toReturnToDaily > 0) {
      lines.push(`${plural(preview.toReturnToDaily, 'spend record', 'spend records')} on a fixed rate that has been removed will return to the daily market rate for ${preview.toReturnToDaily === 1 ? 'its' : 'their'} own date.${preview.toChange > 0 ? '' : ' Reported totals for this period will change. Factors and inflation adjustments stay as they are.'}`);
    }
    if (preview.toChange === 0 && preview.toReturnToDaily === 0) {
      lines.push('No calculated spend records in this period need recalculating.');
    }
    if (preview.uncommittedLedgerLinesToReturn > 0) {
      lines.push(`${plural(preview.uncommittedLedgerLinesToReturn, 'ledger line', 'ledger lines')} awaiting review will be converted at the daily market rate again.`);
    }
    if (preview.uncommittedLedgerLines > 0) {
      lines.push(`${plural(preview.uncommittedLedgerLines, 'ledger line', 'ledger lines')} awaiting review will be converted into your currency at the fixed rate.`);
    }
    if (preview.skippedCount > 0) {
      lines.push(`${plural(preview.skippedCount, 'record', 'records')} will keep their current rate: ${describeReasons(preview.skippedReasons, labels)}.`);
    }
    const notes = describeReasons(preview.notes, labels);
    if (notes) lines.push(`Not changed: ${notes}.`);
    return lines;
  };

  const applyHandler = async () => {
    try {
      setIsPreparingApply(true);
      const preview = await reportPeriodService.getExchangeRateApplyPreview(reportPeriodId);
      setIsPreparingApply(false);
      const records = preview.toChange + preview.toReturnToDaily;
      const ledgerLines = preview.uncommittedLedgerLines + preview.uncommittedLedgerLinesToReturn;
      if (records === 0 && ledgerLines === 0) {
        userNotificationsContext.openSnackbar({ open: true, message: 'Every spend record in this period already matches its exchange rates. Nothing to recalculate.' });
        fetchStranded();
        return;
      }
      const returningOnly = preview.toChange === 0 && preview.uncommittedLedgerLines === 0;
      const confirmed = await userNotificationsContext.openDialog({
        active: true,
        title: returningOnly ? 'Return existing spend to daily market rates' : 'Apply fixed rates to existing spend',
        message: buildConfirmation(preview),
        buttonCopy: records > 0 ? `Recalculate ${plural(records, 'record', 'records')}` : `Convert ${plural(ledgerLines, 'ledger line', 'ledger lines')}`,
        cancelButtonCopy: 'Cancel',
        hideCancelButton: false,
      });
      if (confirmed !== true) return;
      const response = await reportPeriodService.applyExchangeRatesToExistingSpend(reportPeriodId);
      userNotificationsContext.openSnackbar({ open: true, message: returningOnly ? 'Returning existing spend to daily market rates. This runs in the background.' : 'Recalculating existing spend at the fixed rates. This runs in the background.' });
      setPollJobId(response.job.id);
      fetchApplyJobs();
    } catch (err: any) {
      setIsPreparingApply(false);
      userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'There was an error, please try again',
        backgroundColor: 'text.primary',
      });
    }
  };

  // The currency list is the app's own: the currencies on the country list (the same list the
  // organisation currency picker reads), one entry per code.
  useEffect(() => {
    locationService.getAllCountries()
      .then((countries) => {
        const byCode = new Map<string, ICurrencyOption>();
        for (const country of countries || []) {
          if (country.currencyCode && !byCode.has(country.currencyCode)) {
            byCode.set(country.currencyCode, { code: country.currencyCode, symbol: country.currencySymbol });
          }
        }
        setCurrencyOptions([...byCode.values()].sort((a, b) => a.code.localeCompare(b.code)));
      })
      .catch((err) => console.log(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddDialog = () => {
    setEditRate(null);
    setDialogOpen(true);
  };

  const openEditDialog = (id: string) => {
    const rate = rates.find((r) => r.id === id);
    if (!rate) return;
    setEditRate(rate);
    setDialogOpen(true);
  };

  const deleteHandler = (id: string) => {
    const rate = rates.find((r) => r.id === id);
    if (!rate) return;
    userNotificationsContext.openDialog({
      active: true,
      title: 'Delete fixed rate',
      message: `Delete the fixed rate of 1 ${rate.fromCurrencyCode} = ${formatRate(rate.rate)} ${rate.toCurrencyCode}? Spend calculated after this uses the daily market rate again. Records already calculated keep the fixed rate until you return them to daily market rates, from this section.`,
      buttonCopy: 'Delete',
      cancelButtonCopy: 'Cancel',
      hideCancelButton: false,
    })
      .then((confirmed) => {
        if (!confirmed) return;
        reportPeriodService.deleteExchangeRate(reportPeriodId, id)
          .then(() => {
            userNotificationsContext.openSnackbar({ open: true, message: 'Fixed rate deleted' });
            fetchRates();
            fetchStranded();
          })
          .catch((err) => {
            userNotificationsContext.openSnackbar({
              open: true,
              message: err?.response?.data?.message || 'There was an error, please try again',
              backgroundColor: 'text.primary',
            });
          });
      });
  };

  const overrideOn = rates.length > 0 || overrideOpened;

  // Unticking with rates saved removes them, because a saved rate is what overrides. The
  // confirmation says what that does to new and existing spend.
  const overrideToggleHandler = async (checked: boolean) => {
    if (checked) {
      setOverrideOpened(true);
      return;
    }
    if (rates.length === 0) {
      setOverrideOpened(false);
      return;
    }
    const confirmed = await userNotificationsContext.openDialog({
      active: true,
      title: 'Stop using fixed exchange rates',
      message: [
        `${plural(rates.length, 'fixed rate', 'fixed rates')} will be deleted. Spend calculated after this is converted at the daily market rate again.`,
        'Records already calculated keep the fixed rate. You can return them to daily market rates afterwards, from this section.',
      ],
      buttonCopy: `Delete ${plural(rates.length, 'fixed rate', 'fixed rates')}`,
      cancelButtonCopy: 'Cancel',
      hideCancelButton: false,
    });
    if (confirmed !== true) return;
    try {
      for (const rate of rates) {
        await reportPeriodService.deleteExchangeRate(reportPeriodId, rate.id);
      }
      setOverrideOpened(false);
      userNotificationsContext.openSnackbar({ open: true, message: 'Fixed exchange rates removed. New spend uses daily market rates again.' });
    } catch (err: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'There was an error, please try again',
        backgroundColor: 'text.primary',
      });
    } finally {
      fetchRates();
      fetchStranded();
    }
  };

  const headCells: IHeadCell[] = [
    { id: 'fromCurrencyCode', label: 'From', sortable: true, numeric: false },
    { id: 'toCurrencyCode', label: 'To', sortable: true, numeric: false },
    { id: 'rate', label: 'Fixed rate', sortable: false, numeric: false },
    { id: 'rateSource', label: 'Where the rate came from', sortable: false, numeric: false },
    { id: 'action', label: 'Action', sortable: false, numeric: false },
  ];

  const tableRows = useMemo(() => rates.map((rate) => ({
    id: rate.id,
    fromCurrencyCode: rate.fromCurrencyCode,
    toCurrencyCode: rate.toCurrencyCode,
    rate: `1 ${rate.fromCurrencyCode} = ${formatRate(rate.rate)} ${rate.toCurrencyCode}`,
    rateSource: rate.rateSource,
    // Greyed out rather than removed on a closed period, so the controls do not vanish.
    action: (
      <TableActionButtons
        id={rate.id}
        includeEdit={true}
        includeDelete={true}
        onEdit={openEditDialog}
        onDelete={deleteHandler}
        disableButton={isLocked}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [rates, isLocked]);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant='subtitle1' sx={{ color: 'text.primary', mb: 1 }}>Exchange rates</Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
        Spend in another currency is converted automatically, at the daily market rate on each record&apos;s date. You do not need to set anything here.
      </Typography>

      {isLocked && (
        <Alert severity='info' sx={{ mt: 2 }}>
          This reporting period is closed, so its exchange rates cannot be changed. Reopen the period to change them.
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '120px' }}>
          <LoadingSpinner />
        </Box>
      ) : errorMessage ? (
        <Box sx={{ p: 2 }}>
          <Typography color='error'>{errorMessage}</Typography>
          <Button type='button' onClick={fetchRates} sx={{ mt: 2 }}>
            Try Again
          </Button>
        </Box>
      ) : (
        <>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={(
              <Checkbox
                id='override-exchange-rates'
                checked={overrideOn}
                disabled={isLocked || hasActiveJob}
                onChange={(event) => overrideToggleHandler(event.target.checked)}
              />
            )}
            label={<Typography variant='body1'>Override the daily market rate with a fixed rate for this reporting period</Typography>}
          />

          {strandedCount > 0 && !hasActiveJob && (
            <Alert
              severity='warning'
              sx={{ mt: 1, alignItems: 'center' }}
              action={(
                <LoadingButton
                  type='button'
                  variant='outlined'
                  size='small'
                  loading={isPreparingApply}
                  onClick={applyHandler}
                  disabled={isLocked}
                >
                  <Typography variant='body2'>Return to daily market rates</Typography>
                </LoadingButton>
              )}
            >
              {plural(strandedCount, 'record in this period is', 'records in this period are')} still on a fixed rate that has been removed.
            </Alert>
          )}

          {overrideOn && (
            <Box sx={{ mt: 1 }}>
              <Stack direction={isMobileMode ? 'column' : 'row'} justifyContent='space-between' alignItems={isMobileMode ? 'flex-start' : 'center'} gap={1} sx={{ mb: 2 }}>
                <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
                  Each fixed rate replaces the daily market rate for one pair of currencies, for example the rate your historic reports used. Pairs you do not list stay on the daily market rate.
                </Typography>
                <Stack direction='row' gap={1} flexWrap='wrap' sx={{ flexShrink: 0 }}>
                  <LoadingButton
                    type='button'
                    variant='outlined'
                    loading={isPreparingApply}
                    loadingPosition='start'
                    startIcon={<CurrencyExchangeOutlined fontSize='small' />}
                    onClick={applyHandler}
                    disabled={isLocked || (rates.length === 0 && strandedCount === 0) || hasActiveJob}
                  >
                    <Typography sx={{ pl: 0.5 }}>Apply to existing spend</Typography>
                  </LoadingButton>
                  <Button
                    type='button'
                    variant='outlined'
                    startIcon={<AddOutlined fontSize='small' />}
                    onClick={openAddDialog}
                    disabled={isLocked}
                  >
                    <Typography sx={{ pl: 0.5 }}>Add fixed rate</Typography>
                  </Button>
                </Stack>
              </Stack>

              {rates.length === 0 ? (
                <Box sx={{ p: 2, borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px' }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                    No fixed rates yet, so every conversion still uses the daily market rate. Add a fixed rate to override it.
                  </Typography>
                </Box>
              ) : (
                <TableDisplay
                  headCells={headCells}
                  rows={tableRows}
                  selected={selected}
                  setSelected={setSelected}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={tableRows.length}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  isMobileMode={isMobileMode}
                  defaultOrderBy='fromCurrencyCode'
                />
              )}

              <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px', mt: 1.5 }}>
                A fixed rate applies to spend calculated after it is saved. To move records already in this period onto the fixed rates, use Apply to existing spend.
              </Typography>
            </Box>
          )}
        </>
      )}

      {hasActiveJob && (
        <Alert severity='info' sx={{ mt: 2 }}>
          Existing spend in this period is being recalculated at the fixed rates. This runs in the background.
        </Alert>
      )}

      <RecalculationHistoryTable
        title='Recalculation history'
        changeLabel='Rates applied'
        rows={applyJobs.map((job) => ({
          id: job.id,
          createdAt: job.createdAt,
          change: (job.ratesApplied || []).map((rate) => `1 ${rate.fromCurrencyCode} = ${formatRate(rate.rate)} ${rate.toCurrencyCode}`).join(', ') || 'Daily market rates',
          status: job.status,
          outcome: job.status === 'COMPLETED'
            ? `${plural(job.recalculatedCount, 'record', 'records')} changed`
              + (job.returnedToDailyCount > 0 ? ` (${job.returnedToDailyCount} returned to daily market rates)` : '')
              + (job.ledgerLinesRestated > 0 ? `, ${plural(job.ledgerLinesRestated, 'ledger line', 'ledger lines')} converted` : '')
              + (job.skippedCount > 0 ? `, ${job.skippedCount} skipped (${describeReasons(job.skippedReasons, reasonLabels)})` : '')
            : null,
          errorMessage: job.errorMessage,
          beforeTotals: job.beforeTotals,
          afterTotals: job.afterTotals,
        }))}
      />

      <ExchangeRateDialog
        open={dialogOpen}
        reportPeriodId={reportPeriodId}
        currencyOptions={currencyOptions}
        editRate={editRate}
        onClose={() => {
          setDialogOpen(false);
          setEditRate(null);
        }}
        onSaved={fetchRates}
      />
    </Box>
  );
};
